import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link, Navigate, useParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { tokens } from 'shared/theme';
import { ENTITY_NAV_ITEMS } from '../config/entityConfigs';

const SETTINGS_KEYS = [
  'business-unit',
  'business-process',
  'system-owner',
  'vendor',
  'regulation',
];

/** Hidden from Operation hub (available via System Register main nav). */
const OPERATION_EXCLUDED_KEYS = [
  'incident',
  'review',
  'change-management',
  'access-provision',
  'risk-register',
  'document',
];

const DETAIL_ITEMS = ENTITY_NAV_ITEMS.filter((item) => item.key !== 'software');

const GROUP_CONFIG = {
  settings: {
    title: 'Settings',
    description: 'Reference data used across software registrations.',
    keys: SETTINGS_KEYS,
  },
  operation: {
    title: 'Operation',
    description: 'Supporting records and operational workflows.',
    keys: DETAIL_ITEMS.filter(
      (item) =>
        !SETTINGS_KEYS.includes(item.key)
        && !OPERATION_EXCLUDED_KEYS.includes(item.key)
    ).map((item) => item.key),
  },
};

const ManageDetailsGroupPage = () => {
  const { group } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;
  const secondaryMain = theme.palette.secondary.main;

  const cardSurface = isDark ? theme.palette.background.paper : '#ffffff';
  const titleColor = isDark ? colors.grey[100] : primaryMain;
  const bodyColor = isDark ? colors.grey[200] : colors.grey[600];

  const config = GROUP_CONFIG[group];

  const items = useMemo(() => {
    if (!config) return [];
    const keySet = new Set(config.keys);
    return DETAIL_ITEMS.filter((item) => keySet.has(item.key));
  }, [config]);

  if (!config) {
    return <Navigate to="/system-register/manage-details" replace />;
  }

  return (
    <Box m={{ xs: '12px', sm: '16px' }}>
      <Header title={config.title} />

      <Box mb={2}>
        <Typography variant="body2" sx={{ color: bodyColor }}>
          {config.description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md:
              group === 'settings'
                ? 'repeat(3, minmax(0, 1fr))'
                : 'repeat(3, minmax(0, 1fr))',
            lg:
              group === 'settings'
                ? 'repeat(5, minmax(0, 1fr))'
                : 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {items.map((item) => (
          <Card
            key={item.key}
            elevation={0}
            sx={{
              height: '100%',
              minHeight: 148,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: cardSurface,
              border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
              borderRadius: 2,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
              boxShadow: isDark ? 'none' : `0 2px 12px ${alpha(primaryMain, 0.06)}`,
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: secondaryMain,
                boxShadow: `0 8px 24px ${alpha(secondaryMain, isDark ? 0.2 : 0.16)}`,
              },
            }}
          >
            <CardActionArea
              component={Link}
              to={item.path}
              sx={{
                color: 'inherit',
                flex: 1,
                display: 'flex',
                alignItems: 'stretch',
                height: '100%',
                '&:hover': {
                  backgroundColor: alpha(secondaryMain, isDark ? 0.1 : 0.04),
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  boxSizing: 'border-box',
                  '&:last-child': { pb: { xs: 2, sm: 2.25 } },
                }}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap={1.25}
                  sx={{ flex: 1, minHeight: 0 }}
                >
                  <Box flex={1} minWidth={0} display="flex" flexDirection="column">
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        color: titleColor,
                        lineHeight: 1.3,
                        mb: 0.75,
                        minHeight: '1.3em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: bodyColor,
                        lineHeight: 1.45,
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '4.35em',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                  <ArrowForwardIcon
                    sx={{
                      fontSize: 20,
                      mt: 0.25,
                      flexShrink: 0,
                      color: alpha(secondaryMain, 0.85),
                    }}
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ManageDetailsGroupPage;
